import type {
  Environment,
  LockReason,
  PromotionStatus,
  ValidationSeverity,
} from "@/api/hooks";
import type {
  PlacementRefusalCode,
  PlacementVerdictState,
} from "@/api/library";
import type {
  SummaryLayer,
  SummaryLayerConcurrency,
  SummaryLayerVersioning,
  SummaryMappingAuthority,
  SummaryMappingState,
  SummaryOwnership,
  SummaryValueCategoryId,
} from "@/api/summaryStructure";

import type {
  AdoptionIdentitySource,
  SectionAdoptionState,
} from "./sectionAdoption";
import type { FailureClass } from "./validationCodes";

/**
 * Display names for contract enums.
 *
 * Every map here is written `satisfies Record<TheContractUnion, string>`. That
 * one word is the point of the file: when the contract grows an enum member,
 * regeneration turns the omission into a compile error here instead of a raw
 * slug on a clinician's screen. Call sites keep a runtime fallback anyway — the
 * generated types describe the contract, they do not validate the response.
 *
 * The environment vocabulary names two different things: the environment the
 * editor itself runs in and the environment a project targets. Only the label
 * is shared; conflating the values is the mistake ProjectOverviewRoute warns
 * about.
 */
export const ENVIRONMENT_LABEL = {
  local: "Local",
  dev: "Development",
  qa: "QA",
  staging: "Staging",
  prod: "Production",
} satisfies Record<Environment, string>;

/**
 * #27's two promotion axes, each with its own vocabulary.
 *
 * They are separate maps because they are separate questions, and the wording of
 * each is chosen so that no reading of one implies another:
 *
 *  - The request axis is the only one the Editor itself owns.
 *  - `unknown` execution is "Not reported", never "Not started". The Editor has
 *    no visibility into off-band DevOps work and must not claim that nothing is
 *    happening.
 *
 * There is deliberately no third map and no "Promoted" label anywhere in this
 * file. #376 removed the pin-observed axis from the contract outright, because
 * only DevOps observes a tenant's runtime pin and the Editor may not publish a
 * reading of one — and nothing replaces it.
 */
export const PROMOTION_REQUEST_LABEL = {
  none: "Not requested",
  requested: "Requested",
} satisfies Record<PromotionStatus["requestState"], string>;

export const PROMOTION_EXECUTION_LABEL = {
  unknown: "Not reported",
  in_progress: "In progress",
} satisfies Record<PromotionStatus["executionState"], string>;

export const SEVERITY_LABEL = {
  error: "Error",
  warning: "Warning",
  info: "Information",
} satisfies Record<ValidationSeverity, string>;

/**
 * Why a field is locked.
 *
 * Only `safety_floor` is the clinical guard floor. Labelling all three as a
 * safety floor — which the shell did before #7 — tells a clinician that a
 * lifecycle lock is a clinical safety rule, which is both wrong and the kind of
 * wrong that gets acted on.
 */
export const LOCK_REASON_LABEL = {
  safety_floor: "Locked safety floor",
  external_source: "Locked — external source",
  lifecycle: "Locked — lifecycle state",
} satisfies Record<LockReason, string>;

export const LOCK_REASON_DETAIL = {
  safety_floor:
    "The clinical safety floor is enforced by the engine and cannot be weakened by an overlay.",
  external_source:
    "This value comes from a system outside the editor, so it cannot be changed here.",
  lifecycle:
    "The project's current lifecycle state does not allow this value to change.",
} satisfies Record<LockReason, string>;

/**
 * The placement registry's vocabulary for one pin, in a clinician's words
 * (#631/#632, consumed for #612's refused-replacement rendering).
 *
 * `satisfies` keeps the maps complete against the GENERATED union, as every
 * map in this file is — but these two are the one place that guarantee is not
 * the whole story. The contract's own note on `PlacementRefusalCode` says the
 * registry's vocabulary widens with POLICY versions, not with this contract,
 * so a deployed service may answer a member the generated types have never
 * seen. Call sites must therefore read these with a fallback to the raw code
 * (`LABEL[code] ?? code`), exactly as `SectionReviewList` reads
 * `LOCK_REASON_LABEL` — a raw registry slug on screen is ugly; a crash inside
 * a refusal explanation is a recovery surface that fails when it is needed.
 */
export const PLACEMENT_VERDICT_LABEL = {
  placeable: "Placeable in this project",
  protected: "Protected — part of the mandatory floor",
  archived: "Archived in the Clinical library",
  unavailable: "Not available to this project",
  runtime_unreachable: "Not reachable by this project's runtime",
  unsupported: "Not supported by the placement registry",
} satisfies Record<PlacementVerdictState, string>;

export const PLACEMENT_REFUSAL_LABEL = {
  "placement.family_not_pack_content": "This family is not pack content",
  "placement.family_protected_floor": "This family is the protected floor",
  "placement.family_fixed_pack_path": "This family has a fixed pack path",
  // AUTHORING since #638, not adoption. The family's approved inventory is
  // adoptable — this code now states that its assets cannot be CREATED here.
  "placement.family_governance_gated":
    "This family cannot be authored in the Editor",
  "placement.coordinate_missing": "The version has no placement coordinate",
  "placement.coordinate_shape_unsupported":
    "The placement coordinate's shape is not supported",
  "placement.asset_archived": "The asset is archived",
  "placement.asset_locked_floor": "The asset is the locked safety floor",
  "placement.asset_uncatalogued": "The asset is not in the catalog",
  "placement.asset_read_only": "The asset is read-only",
  "placement.asset_not_licensed_inventory":
    "The instrument is not in the approved clinical inventory",
  "placement.asset_license_withdrawn":
    "The instrument is no longer licensed for new adoption",
  "placement.content_incomplete": "The stored content is incomplete",
  "placement.runtime_profile_unresolved":
    "The project's runtime profile did not resolve",
  "placement.runtime_engine_unresolved": "The runtime engine did not resolve",
  "placement.runtime_engine_mismatch":
    "The version targets a different runtime engine",
  "placement.runtime_profile_mismatch":
    "The version targets a different runtime profile",
  "placement.runtime_unreachable":
    "The placement is not reachable by this project's runtime",
  "placement.runtime_engine_unsupported":
    "The runtime engine does not support this placement",
  "placement.ownership_conflict":
    "Another adopted asset already owns this runtime placement",
  "placement.background_roster_conflict":
    "The adopted background domains cannot run together in one interview",
  "placement.background_profile_conflict":
    "The background profile and the adopted domains do not describe one complete interview",
  "placement.alert_destination_reference_invalid":
    "The Alert version contains endpoint or credential material",
  "placement.alert_destination_unknown":
    "An Alert destination is not in the current authority",
  "placement.alert_destination_revoked":
    "An Alert destination has been revoked",
  "placement.alert_destination_environment_scope":
    "An Alert destination is not authorized in this environment",
  "placement.alert_destination_project_scope":
    "An Alert destination is not authorized for this project",
  "placement.family_unrecognized":
    "The registry does not recognize this family",
  "placement.cardinality_exceeded":
    "The set exceeds this family's active cardinality",
  "placement.pathway_parent_required":
    "The Pathway variant requires its matching family default",
} satisfies Record<PlacementRefusalCode, string>;

/**
 * Failure classes are a frontend inference from a code prefix, not a contract
 * enum — see `lib/validationCodes.ts`. The `unclassified` label states that
 * plainly rather than reading like a category of its own.
 */
export const FAILURE_CLASS_LABEL = {
  schema: "Schema",
  compatibility: "Compatibility",
  base_identity: "Base identity",
  locked_floor: "Locked safety floor",
  adopted_content: "Adopted content",
  safety_evidence: "Safety confirmation",
  unclassified: "Unclassified",
} satisfies Record<FailureClass, string>;

/**
 * What one clinical section adopted, in a clinician's words (#443).
 *
 * Five states the ticket requires to be told apart, and they are told apart in
 * TEXT rather than by tone alone — the badge below carries a colour, but the
 * label and the sentence carry the whole meaning, so the distinction survives
 * a monochrome screen and a screen reader.
 *
 * `not-applicable` reads as a statement about this section rather than as an
 * absence: "adopts nothing" is a true and useful answer for a section that
 * serves no fields and carries no refs, and it must never be what an unread
 * projection renders — which is why `unavailable` says the read failed instead.
 */
export const ADOPTION_STATE_LABEL = {
  current: "Current",
  converging: "Converging",
  outdated: "Outdated",
  overridden: "Locally overridden",
  unavailable: "Unavailable",
  "not-applicable": "Adopts nothing",
} satisfies Record<SectionAdoptionState, string>;

export const ADOPTION_STATE_DETAIL = {
  current:
    "Every shared-library item supplying this section is adopted at the library's head version, and nothing in this project overrides it.",
  converging:
    "This section was computed from an earlier project pin set than the one currently loaded. The reads are settling, so this is not reported as current or outdated yet.",
  outdated:
    "The shared library has published a newer version of at least one item supplying this section. This project keeps running the version it adopted until the pin is moved.",
  overridden:
    "This project changes values away from what it adopted. The Editor Service reports the change; nothing here compares content.",
  unavailable:
    "This section's adoption state could not be read, so nothing below claims what it adopts.",
  "not-applicable":
    "The Editor Service reports no shared-library item supplying this section. Its clinical configuration comes from the base pack alone.",
} satisfies Record<SectionAdoptionState, string>;

/**
 * The headline detail for an `overridden` section whose supplying identity the
 * service did not report — `SectionAdoption.adoptedIdentityErased`.
 *
 * It replaces `ADOPTION_STATE_DETAIL.overridden` there, and only there, because
 * that sentence presupposes an adoption ("away from what it adopted") which
 * this response cannot establish: an adoption this project has fully overridden
 * and a section that adopts nothing and was then edited locally produce the
 * SAME response. What is reported is the change, so that is all this says.
 *
 * It deliberately does NOT restate "and no item supplying it" — the paragraph
 * the block renders underneath opens on exactly that, and a headline repeating
 * its own next sentence reads as a rendering bug.
 */
export const IDENTITY_ERASED_DETAIL =
  "This section's values are reported as locally changed. The Editor Service reports the change; nothing here compares content.";

/**
 * Where a section's adopted identity was read from.
 *
 * Named on the surface rather than assumed, because the three shapes are the
 * one thing an implementation of this feature gets wrong: `alerts` serves no
 * fields and `pathways` serves refs only, so a surface reading field origins
 * alone would report both as adopting nothing.
 */
export const IDENTITY_SOURCE_LABEL = {
  alert_matrices: "the section's adopted alert matrices",
  pathway_refs: "the section's adopted pathway references",
  field_origin: "the effective fields' reported origin",
} satisfies Record<AdoptionIdentitySource, string>;

// #153: the product-settings label vocabulary (source-of-truth, promotion
// target, previewability, patient-entry modes) left with the Product Settings
// surface. Those values are Tenant Manifest domain and have no rendering here.

/**
 * The Summary three-layer vocabulary, in a clinician's words (#639's contract,
 * #640's surface).
 *
 * Six closed enums, six `satisfies Record<…>` maps, and no fallback at the call
 * sites — deliberately unlike `PLACEMENT_REFUSAL_LABEL` next door, and for a
 * reason the contract states itself. That vocabulary widens with POLICY
 * versions the generated types have never seen, so a raw slug is the honest
 * degraded rendering. These do not: `SummaryOwnership`'s own contract note says
 * "a fourth class would mean the layer model itself changed, and a client
 * switching on this enum should stop compiling rather than guess", and the
 * registry's version pin (`SUMMARY_STRUCTURE_CLIENT_VERSION`) refuses a
 * deployment serving a structure this build has not seen. Between the two, a
 * member with no label here cannot reach a screen.
 *
 * Every sentence below is the contract's own statement of the value, condensed.
 * None of it is a browser's opinion about what a clinician may do — that
 * question is answered by `writeRoute` in `lib/summaryStructure.ts`.
 */
export const SUMMARY_LAYER_LABEL = {
  prompt_authoring: "Prompt and content authoring",
  runtime_projection: "Runtime clinical projection",
  presentation_rendering: "Presentation and rendering",
} satisfies Record<SummaryLayer, string>;

export const SUMMARY_OWNERSHIP_LABEL = {
  clinician_editable: "Clinician-editable",
  target_renderer_owned: "Target/renderer-owned",
  protected_runtime: "Protected runtime",
} satisfies Record<SummaryOwnership, string>;

export const SUMMARY_OWNERSHIP_DETAIL = {
  clinician_editable:
    "Authored through the Clinical library's own write surface: immutable versions, explicit exact-version adoption, never edited in place.",
  target_renderer_owned:
    "Derived from the preview target or owned by the rendering surface. Read-only wherever the runtime record is concerned.",
  protected_runtime:
    "The runtime clinical record and its protected configuration. Served with provenance and lock state, and never written directly by any client.",
} satisfies Record<SummaryOwnership, string>;

export const SUMMARY_VERSIONING_LABEL = {
  immutable_asset_versions: "Immutable asset versions and the pin set",
  adoptions_version_and_base_identity:
    "Pin-set version and exact base identity",
  registry_version_and_project_revision:
    "Registry identity and Project presentation version",
} satisfies Record<SummaryLayerVersioning, string>;

export const SUMMARY_CONCURRENCY_LABEL = {
  compare_and_set_writes: "Writes are refused when stale",
  identity_checked_reads: "Reads fail closed on a disagreeing identity",
  registry_checked_compare_and_set_writes:
    "Registry mismatches and stale Project writes are refused",
} satisfies Record<SummaryLayerConcurrency, string>;

/**
 * The runtime projection's value categories.
 *
 * The wording is the category's own contract sentence, not an interpretation of
 * the values inside it — this surface never reads those values, and naming a
 * category is not describing its content.
 */
export const SUMMARY_CATEGORY_LABEL = {
  generation: "Generation settings",
  output_policy: "Output policy",
  fixed_values: "Fixed wording",
  labels: "Display labels",
  per_key: "Per-key instructions",
} satisfies Record<SummaryValueCategoryId, string>;

export const SUMMARY_MAPPING_STATE_LABEL = {
  established: "Mapped",
  not_established: "No authoritative mapping",
} satisfies Record<SummaryMappingState, string>;

/**
 * Where an established mapping comes from. Both members are cited by name on
 * screen because they are different strengths of evidence: one is a recorded
 * identity pairing, the other is a placement the packaged seed catalog proves.
 */
export const SUMMARY_MAPPING_AUTHORITY_LABEL = {
  parity_audit: "Recorded by the 2026-08-28 parity audit",
  seed_manifest: "Proved by the packaged seed catalog",
} satisfies Record<SummaryMappingAuthority, string>;
